// servizi/admob.ts — v046: la pubblicità nelle app Android e iOS con AdMob (plugin @capacitor-community/admob). Sul
// web resta AdSense (servizi/pubblicita.ts, gioco/AnnuncioRisultato.tsx). Solo per chi è in Base, come sul web; in
// partita decide anche il server (pubblicitaConsentita, in AnnuncioRisultato.tsx).
// - banner (fine partita, attesa dell'altro): un riquadro nativo in fondo allo schermo, SOPRA la pagina. Mentre c'è,
//   la sua altezza sta nella variabile CSS --ld-banner-admob (px) di <html> (0px quando non c'è).
// - interstiziale (a tutto schermo): la pagina «prima di giocare» (gioco/PubblicitaPrima.tsx).
// Gli ID delle unità sono impostazioni pubbliche (app.admob_banner_* e app.admob_interstiziale_*): vuoti = spento.
// Con app.admob_prova accesa si usano SEMPRE gli ID di prova di Google (toccare annunci veri durante le prove fa
// chiudere l'account AdMob). Il consenso GDPR (UMP) si chiede la prima volta che serve un annuncio, con il messaggio
// configurato in AdMob → Privacy e messaggi. Nel browser (e nel banco di prova) tutto è spento e non carica niente.
import { NATIVA } from "./nativo.ts";
import { pubblicitaAttiva } from "./pubblicita.ts";
import { statoAccount } from "./account.ts";
import { eBooleano, eTesto, leggiImpostazione } from "./impostazioni.ts";
import { IMP_APP, PIATTAFORMA } from "../costanti.ts";

export type PostoBanner = "risultato" | "attesa";
type Unita = "banner" | "interstiziale";

/** ID di prova ufficiali di Google (https://developers.google.com/admob/android/test-ads e …/ios/test-ads). */
const ID_PROVA: Record<"android" | "ios", Record<Unita, string>> = {
  android: { banner: "ca-app-pub-3940256099942544/9214589741", interstiziale: "ca-app-pub-3940256099942544/1033173712" },
  ios: { banner: "ca-app-pub-3940256099942544/2435281174", interstiziale: "ca-app-pub-3940256099942544/4411468910" },
};
const FORMA_ID = /^ca-app-pub-\d{10,20}\/\d{5,15}$/;
/** Quanto si aspetta che l'annuncio a tutto schermo si carichi prima di andare avanti senza. */
const ATTESA_INTERSTIZIALE_MS = 8000;

const inProva = (): boolean => leggiImpostazione(IMP_APP.admobProva, false, eBooleano);

/** L'ID dell'unità per questa piattaforma ("" = spento). */
export function idUnitaAdMob(unita: Unita): string {
  if (PIATTAFORMA !== "android" && PIATTAFORMA !== "ios") return "";
  if (inProva()) return ID_PROVA[PIATTAFORMA][unita];
  const chiave =
    unita === "banner"
      ? PIATTAFORMA === "android" ? IMP_APP.admobBannerAndroid : IMP_APP.admobBannerIos
      : PIATTAFORMA === "android" ? IMP_APP.admobInterstizialeAndroid : IMP_APP.admobInterstizialeIos;
  const id = leggiImpostazione(chiave, "", eTesto).trim();
  return FORMA_ID.test(id) ? id : "";
}

/** Chi guarda è in Base (o non ha l'account) e la pubblicità è accesa? */
const perChiGuarda = (): boolean => {
  const s = statoAccount();
  return pubblicitaAttiva() && !(s.tipo === "registrato" && s.io.piano !== "base");
};

/** C'è la pubblicità AdMob per questa unità qui e per chi guarda? (Nel browser sempre false.) */
export function admobDisponibile(unita: Unita): boolean {
  return NATIVA && perChiGuarda() && idUnitaAdMob(unita) !== "";
}

type Plugin = typeof import("@capacitor-community/admob");
let avvio: Promise<Plugin | null> | null = null;
let privacyRichiesta = false;
let inizializzato = false;

/**
 * Inizializza AdMob una volta sola e chiede il consenso (UMP) se serve. Restituisce il plugin se si possono
 * chiedere annunci, null altrimenti (browser, plugin assente, consenso non dato). Si può chiamare all'avvio dell'app
 * per far comparire il messaggio del consenso subito invece che alla prima pubblicità.
 */
export function avviaAdMob(): Promise<Plugin | null> {
  if (!NATIVA) return Promise.resolve(null);
  avvio ??= (async () => {
    try {
      const p = await import("@capacitor-community/admob");
      const { AdMob, AdmobConsentStatus } = p;
      if (!inizializzato) {
        await AdMob.initialize({ initializeForTesting: inProva() });
        void AdMob.addListener(p.BannerAdPluginEvents.SizeChanged, (d) => altezzaBanner(d.height));
        inizializzato = true;
      }
      let info = await AdMob.requestConsentInfo();
      if (info.isConsentFormAvailable && info.status === AdmobConsentStatus.REQUIRED) info = await AdMob.showConsentForm();
      // (l'enum non è esportato dal pacchetto: si confronta il valore)
      privacyRichiesta = String(info.privacyOptionsRequirementStatus) === "REQUIRED";
      if (!info.canRequestAds) {
        avvio = null; // si richiede la prossima volta (magari ha cambiato idea dalle opzioni della privacy)
        return null;
      }
      return p;
    } catch {
      avvio = null; // build dell'app senza il plugin o senza rete: niente pubblicità, si riprova la prossima volta
      return null;
    }
  })();
  return avvio;
}

/**
 * Il GDPR vuole un modo per cambiare il consenso dato: se è true l'app deve mostrare un pulsante (es. nelle
 * impostazioni) che chiama apriPrivacyAdMob(). Vale dopo avviaAdMob().
 */
export const privacyAdMobRichiesta = (): boolean => NATIVA && privacyRichiesta;

/** Apre il modulo di Google per cambiare il consenso alla pubblicità. */
export async function apriPrivacyAdMob(): Promise<void> {
  if (!NATIVA) return;
  try {
    const { AdMob } = await import("@capacitor-community/admob");
    await AdMob.showPrivacyOptionsForm();
  } catch {
    // modulo non disponibile: niente
  }
  avvio = null; // al prossimo annuncio si rilegge il consenso
}

function altezzaBanner(px: number): void {
  if (typeof document === "undefined") return;
  document.documentElement.style.setProperty("--ld-banner-admob", `${Math.max(0, Math.round(px))}px`);
}

// Un solo banner per volta (il plugin ne ha uno): chi lo ha messo e una coda perché mostra/togli non si incrocino.
let bannerDi: PostoBanner | null = null;
let coda: Promise<unknown> = Promise.resolve();

/**
 * Mostra (`mostra` true) o toglie il banner del posto. Toglie solo se il banner è ancora di quel posto (un'altra
 * pagina potrebbe averlo già preso). Restituisce true se il banner è stato chiesto e caricato.
 */
export function bannerAdMob(posto: PostoBanner, mostra: boolean): Promise<boolean> {
  if (!NATIVA) return Promise.resolve(false);
  const lavoro = coda.then(async (): Promise<boolean> => {
    if (!mostra) {
      if (bannerDi !== posto) return false;
      bannerDi = null;
      altezzaBanner(0);
      try {
        const { AdMob } = await import("@capacitor-community/admob");
        await AdMob.removeBanner();
      } catch {
        // già tolto: niente
      }
      return false;
    }
    const id = idUnitaAdMob("banner");
    if (!id || !perChiGuarda()) return false;
    const p = await avviaAdMob();
    if (!p) return false;
    try {
      bannerDi = posto;
      await p.AdMob.showBanner({
        adId: id,
        adSize: p.BannerAdSize.ADAPTIVE_BANNER,
        position: p.BannerAdPosition.BOTTOM_CENTER,
        margin: 0,
        isTesting: inProva(),
      });
      return bannerDi === posto;
    } catch {
      if (bannerDi === posto) bannerDi = null;
      altezzaBanner(0);
      return false;
    }
  });
  coda = lavoro.catch(() => undefined);
  return lavoro;
}

/**
 * L'annuncio a tutto schermo (pagina «prima di giocare»). Si risolve quando chi guarda lo chiude (true), o subito con
 * false se non c'è (browser, ID vuoto, piano non Base, consenso negato, non caricato entro qualche secondo): in quel
 * caso si va avanti senza aspettare.
 */
let interstizialeInCorso: Promise<boolean> | null = null;
export function interstizialeAdMob(): Promise<boolean> {
  // una sola volta anche se chiamato due volte di fila (React in sviluppo ripete gli effetti)
  interstizialeInCorso ??= mostraInterstiziale().finally(() => (interstizialeInCorso = null));
  return interstizialeInCorso;
}

async function mostraInterstiziale(): Promise<boolean> {
  if (!admobDisponibile("interstiziale")) return false;
  const p = await avviaAdMob();
  if (!p) return false;
  const { AdMob, InterstitialAdPluginEvents } = p;
  const maniglie: { remove(): unknown }[] = [];
  try {
    const caricato = await Promise.race([
      AdMob.prepareInterstitial({ adId: idUnitaAdMob("interstiziale"), isTesting: inProva() }).then(() => true),
      new Promise<false>((ok) => setTimeout(() => ok(false), ATTESA_INTERSTIZIALE_MS)),
    ]);
    if (!caricato) return false;
    let fine: (si: boolean) => void = () => undefined;
    const chiuso = new Promise<boolean>((ok) => (fine = ok));
    maniglie.push(await AdMob.addListener(InterstitialAdPluginEvents.Dismissed, () => fine(true)));
    maniglie.push(await AdMob.addListener(InterstitialAdPluginEvents.FailedToShow, () => fine(false)));
    await AdMob.showInterstitial();
    return await chiuso;
  } catch {
    return false; // non caricato o non mostrato: si va avanti
  } finally {
    for (const h of maniglie) void h.remove();
  }
}
